import React, { useState } from 'react'
import { defineMessages, IntlShape, useIntl } from 'react-intl'
import { Button, Box, Text, Stack, useToast } from '@homebotapp/hb-react-component-catalog'
import { formatStreetAddress } from '../../../../../helpers/formatListingAddress'
import { formatCustomerProfileType } from '../../../../../helpers/formatCustomerProfileType'
import { PreferredPartnerCard } from '../../../HomebotNetwork/PreferredPartnerCard'
import { verifyTeamMembership } from '../../../../../store/slices/homebotNetwork/clientTeamMemberships'
import { useDispatch, useSelector } from 'react-redux'
import { useVerificationFlow } from '../../VerificationFlowContext'
import { AppDispatch } from '../../../../../store'
import { selectAddress, selectHomeId } from '../../../../../store/selectors/home'
import { usePrequalRequest } from '../../../../../hooks/usePrequalRequest'
import { CUSTOMER_MESSAGES } from '../../../../../constants/customerMessages'
import { useVerificationPrequalStep } from '../../../../../hooks/homebotNetwork/verificationFlow/useVerificationPrequalStep'

const MSG = defineMessages({
  add: {
    id: 'VerificationFlow.UnverifiedOptions.add',
    defaultMessage: 'Yes, add'
  },
  dontWantToWorkWith: {
    id: 'VerificationFlow.UnverifiedOptions.dontWantToWorkWith',
    defaultMessage: "Don't want to work with {partnerName}? You can"
  },
  error: {
    id: 'VerificationFlow.UnverifiedOptions.error',
    defaultMessage: 'An error occurred while verifying the partner.'
  },
  heading: {
    id: 'VerificationFlow.UnverifiedOptions.heading',
    defaultMessage: 'We found your {customerType} from {streetAddress}. Would you like to add them to the conversation?'
  },
  notAtThisTime: {
    id: 'VerificationFlow.UnverifiedOptions.notAtThisTime',
    defaultMessage: 'Not at this time'
  },
  readyForPrequal: {
    id: 'VerificationFlow.UnverifiedOptions.readyForPrequal',
    defaultMessage: 'Ready to request a pre-qual?'
  },
  remove: {
    id: 'VerificationFlow.UnverifiedOptions.remove',
    defaultMessage: 'No, remove'
  },
  removePartner: {
    id: 'VerificationFlow.UnverifiedOptions.removePartner',
    defaultMessage: 'remove'
  },
  subTextRea: {
    id: 'VerificationFlow.UnverifiedOptions.subTextRea',
    defaultMessage:
      "If so, we'll keep them on your team, and they'll be kept in the loop with real estate related questions."
  },
  subTextLo: {
    id: 'VerificationFlow.UnverifiedOptions.subTextLo',
    defaultMessage: "We'll keep them on your team to help with finance questions."
  },
  terms: {
    id: 'VerificationFlow.UnverifiedOptions.terms',
    defaultMessage:
      "By clicking 'Yes, Add' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the agent identified above. You agree that the agent may contact you about your inquiry."
  }
})

/**
 * VerificationPrequalView Component
 *
 * Specific UI for the prequal flow with loan officers
 */
const VerificationPrequalView = () => {
  const intl = useIntl()
  const { onSkip, onRemove, verifyAndSendPrequal, isLoading, membership } = useVerificationPrequalStep()
  const streetAddress = useSelector(selectAddress)
  const toast = useToast()

  const handleVerifyAndSendPrequal = async () => {
    try {
      await verifyAndSendPrequal()
    } catch (error) {
      toast({
        description: intl.formatMessage(MSG.error),
        status: 'error'
      })
    }
  }

  return (
    <Box>
      <Box data-qa='verification-prequal-view' data-testid='verification-prequal-view'>
        <PreferredPartnerCard partner={membership.customerProfile} corporateProfile={membership.corporateProfile} />
        <Text fontWeight='semibold' color='neutral.900' mt={8}>
          {intl.formatMessage(MSG.heading, {
            streetAddress: formatStreetAddress(streetAddress),
            customerType: formatCustomerProfileType('LoanOfficer')
          })}
        </Text>
        <Text size='sm' color='neutral.800' mb='7'>
          {intl.formatMessage(MSG.subTextLo)}
        </Text>

        <Text size='lg' fontWeight='700' color='neutral.900' mb='4'>
          {intl.formatMessage(MSG.readyForPrequal)}
        </Text>

        <Stack direction='row' gap={2} justifyContent='flex-end' mt='6'>
          <Button
            variant='outline'
            colorScheme='primary'
            onClick={onSkip}
            isDisabled={isLoading}
            flex={1}
            tracking={{
              guid: '4NQPHqtu8Q9gsYRtYvVLJg',
              ui_context: 'verification_flow_verification_prequal_view',
              descriptor: MSG.notAtThisTime.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.notAtThisTime)}
          </Button>
          <Button
            isLoading={isLoading}
            onClick={handleVerifyAndSendPrequal}
            flex={1}
            colorScheme='primary'
            tracking={{
              guid: 'g34ZnknrJ4H7Lg37t3KTLC',
              ui_context: 'verification_flow_verification_prequal_view',
              descriptor: MSG.add.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.add)}
          </Button>
        </Stack>

        <Text size='sm' color='neutral.700' mt={4}>
          {intl.formatMessage(MSG.dontWantToWorkWith, {
            partnerName: `${membership.customerProfile.firstName} ${membership.customerProfile.lastName}`
          })}{' '}
          <Button
            size='xs'
            colorScheme='primary'
            onClick={onRemove}
            variant='link'
            tracking={{
              guid: 'fYfi2wmncaPaMdbiCokFWN',
              ui_context: 'verification_flow_verification_prequal_view',
              descriptor: MSG.removePartner.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.removePartner)}
          </Button>
          {'.'}
        </Text>
      </Box>
      <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
        <Text size='3xs' color='neutral.600'>
          {intl.formatMessage(MSG.terms)}
        </Text>
      </Box>
    </Box>
  )
}

export default VerificationPrequalView
